/**
 * Reminders — card #99.
 *
 * A reminder is **personal**: the person who sets it is the only one it
 * notifies and the only one who sees it, like My Tasks snooze (§7.5). It comes
 * in two shapes:
 *
 *  - **fixed**: a moment (`at`), which fires once;
 *  - **relative to the due date**: `daysBefore` the due date at `time` in the
 *    owner's timezone, which moves when the due date moves and fires again for
 *    a new one.
 *
 * And each person may set a **default** relative reminder in their
 * notification settings, which applies to every card assigned to them that has
 * a due date unless they switch it off for that card.
 */
import { z } from 'zod';
import { optionalText, uuidSchema } from './common.js';
/** How far ahead of a due date a relative reminder may be. */
export const REMINDER_DAYS_BEFORE_MAX = 30;
export const REMINDER_NOTE_MAX = 500;
/** What a relative reminder is set for when nobody says. */
export const DEFAULT_REMINDER_TIME = '09:00';
/**
 * How late a relative reminder may still fire. A due date set to today for a
 * "9am on the day" reminder at 11am fires at once, which is what someone who
 * just set it wants; a due date moved into last week does not ping about a day
 * that has gone. Fixed reminders have no grace: someone asked for that moment,
 * so a worker that was down at the time sends it late rather than never.
 */
export const REMINDER_LATE_GRACE_HOURS = 12;
/** "09:00" — 24-hour, in the owner's timezone. */
export const reminderTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Times are HH:MM, 24-hour');
export const relativeReminderSchema = z.object({
    daysBefore: z.number().int().min(0).max(REMINDER_DAYS_BEFORE_MAX),
    time: reminderTimeSchema,
});
export const REMINDER_OUTCOMES = ['sent', 'skipped'];
export const reminderSchema = z.object({
    id: uuidSchema,
    taskId: uuidSchema,
    /** Set for a fixed reminder; null for one relative to the due date. */
    at: z.string().nullable(),
    /** Set for a relative reminder; null for a fixed one. */
    daysBefore: z.number().int().nullable(),
    time: z.string().nullable(),
    note: z.string().nullable(),
    /**
     * When it will next fire. Null once a fixed reminder has fired, and for a
     * relative one while the card has no due date or has already been reminded
     * about this one.
     */
    nextAt: z.string().nullable(),
    /** When it last fired, and whether that reached anyone. */
    firedAt: z.string().nullable(),
    outcome: z.enum(REMINDER_OUTCOMES).nullable(),
});
/** Your default reminder as it applies to one card. */
export const cardDefaultReminderSchema = relativeReminderSchema.extend({
    /** Switched off for this card. */
    skipped: z.boolean(),
    nextAt: z.string().nullable(),
    firedAt: z.string().nullable(),
});
export const cardRemindersSchema = z.object({
    reminders: z.array(reminderSchema),
    /**
     * Null unless you have a default reminder *and* the card is assigned to you,
     * which is the only case it applies to.
     */
    default: cardDefaultReminderSchema.nullable(),
});
export const createReminderRequestSchema = z.union([
    z
        .object({
        at: z.string().datetime({ offset: true }),
        note: optionalText(REMINDER_NOTE_MAX),
    })
        .strict(),
    relativeReminderSchema.extend({ note: optionalText(REMINDER_NOTE_MAX) }).strict(),
]);
export const setDefaultReminderSkipRequestSchema = z.object({ skipped: z.boolean() }).strict();
/** One of your reminders, with the card it is on — `GET /me/reminders`. */
export const myReminderSchema = reminderSchema.extend({
    /** Your default reminder on a card, which has no id of its own. */
    isDefault: z.boolean(),
    id: uuidSchema.nullable(),
    task: z.object({ id: uuidSchema, number: z.number().int(), title: z.string() }),
});
// -------------------------------------------------------------------- push
/**
 * A browser's Web Push subscription, as `PushSubscription.toJSON()` gives it.
 * The endpoint is the push service's URL for one browser on one device.
 */
export const createPushSubscriptionRequestSchema = z
    .object({
    endpoint: z.string().url().max(2048),
    keys: z.object({
        p256dh: z.string().min(1).max(256),
        auth: z.string().min(1).max(256),
    }),
    /** "Chrome on macOS" — so a list of devices is a list you can recognise. */
    label: optionalText(200),
})
    .strict();
export const pushSubscriptionSchema = z.object({
    id: uuidSchema,
    label: z.string().nullable(),
    createdAt: z.string(),
    lastSuccessAt: z.string().nullable(),
    lastFailureAt: z.string().nullable(),
    /** The push service's host, which is what a person can recognise. */
    service: z.string(),
    /**
     * The endpoint itself — only ever shown to its owner — so a browser can tell
     * which entry in the list is itself.
     */
    endpoint: z.string(),
});
/** `GET /me/push` — whether this Slate can push at all, and your devices. */
export const pushStateSchema = z.object({
    /** The VAPID public key a browser subscribes with; null when push is not configured. */
    publicKey: z.string().nullable(),
    subscriptions: z.array(pushSubscriptionSchema),
});
export const pushTestResultSchema = z.object({
    sent: z.number().int(),
    failed: z.number().int(),
});
//# sourceMappingURL=reminders.js.map